import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { calculateSalary } from '@amigodoclt/core';
import { Icon } from '../components/Icon.js';
import { NumericInput, Segmented } from '../components/Field.js';
import { ResultCard } from '../components/ResultCard.js';
import { money } from '../lib/format.js';
import { salaryInputOf, useSalaryForm, type SalaryForm } from '../lib/salary-form.js';
import { useReveal } from '../lib/motion.js';

const irrfLabels: Record<SalaryForm['irrfMode'], string> = {
  auto: 'IR mais vantajoso',
  simplified: 'IR simplificado',
  legal: 'IR por deduções legais',
};

/** Lê o formulário do salário para o usuário conferir o que está sendo aplicado nos dois cenários. */
function premisesOf(form: SalaryForm) {
  const items = [
    `Competência ${form.competence}`,
    `${form.hours} h mensais`,
    `${form.dependents} dependente${form.dependents === 1 ? '' : 's'}`,
    irrfLabels[form.irrfMode],
  ];
  if (form.bonusValue > 0) items.push(`Bônus ${form.bonusType === 'fixed' ? money(form.bonusValue) : `${form.bonusValue}% do salário`}`);
  form.overtimes.forEach((row) => items.push(`${row.label ?? 'Hora extra'}: ${row.hours} h a ${row.premiumPercent ?? 50}%`));
  if (form.nightHours > 0) items.push(`${form.nightHours} h noturnas a ${form.nightPremium}%`);
  if (form.vtEnabled) items.push(`Vale-transporte ${form.vtPercent}%`);
  if (form.pension > 0) items.push(`Pensão ${money(form.pension)}`);
  form.deductions.forEach((row) => items.push(`${row.label}: −${money(row.amount)}`));
  return items;
}

function Money({ label, value, onChange, badge }: { label: string; value: number; onChange: (value: number) => void; badge: string }) {
  return (
    <div className="compare-input glass">
      <span className="compare-input-head">
        <span className={`compare-badge${badge === 'B' ? ' b' : ''}`}>{badge}</span>
        {label}
      </span>
      <span className="field-shell">
        <NumericInput className="field numeric with-suffix" label={label} value={value} onChange={onChange} />
        <span className="field-suffix">R$</span>
      </span>
    </div>
  );
}

export function Compare() {
  const root = useRef<HTMLDivElement>(null);
  const [form] = useSalaryForm();
  useReveal(root);

  const a = form.base;
  const [b, setB] = useState(() => Math.round(form.base * 1.5));
  const [shown, setShown] = useState<'a' | 'b'>('a');

  const resultA = useMemo(() => calculateSalary(salaryInputOf(form, a)), [a, form]);
  const resultB = useMemo(() => calculateSalary(salaryInputOf(form, b)), [b, form]);
  const premises = premisesOf(form);

  const delta = resultB.net - resultA.net;
  const up = delta >= 0;
  const percent = resultA.net > 0 ? Math.abs(delta / resultA.net) * 100 : 0;

  return (
    <main className="page" ref={root}>
      <div className="compare-head" data-reveal>
        <p className="eyebrow centered">Cenários</p>
        <h1 className="page-title">Compare dois salários</h1>
        <p className="lead">As mesmas premissas valem para os dois cenários; varia apenas o salário-base.</p>
        <div className="page-actions">
          <button type="button" className="btn-secondary" onClick={() => window.print()}>
            <Icon name="print" size={18} />Imprimir comparação
          </button>
        </div>
      </div>

      <div className="compare-inputs" data-reveal>
        <div className="compare-input glass">
          <span className="compare-input-head"><span className="compare-badge">A</span>Seu salário hoje</span>
          <strong className="compare-input-value money">{money(a)}</strong>
          <span className="helper"><Icon name="payments" size={16} />Vem da calculadora de salário; edite no atalho do topo.</span>
        </div>
        <Money label="Salário do cenário B" value={b} onChange={setB} badge="B" />
      </div>

      <div className="compare-premises glass" data-reveal>
        <div className="compare-premises-head">
          <span className="compare-input-head"><Icon name="sync_alt" size={18} />Premissas vindas do salário líquido</span>
          <Link className="btn-secondary" to="/calculos/salario"><Icon name="tune" size={18} />Ajustar lá</Link>
        </div>
        <div className="chips">
          {premises.map((item) => <span className="chip info" key={item}>{item}</span>)}
        </div>
        <p className="helper">
          <Icon name="info" size={16} />
          Bônus, horas extras, adicional noturno, vale-transporte, pensão e descontos personalizados são os mesmos da
          calculadora de salário líquido e valem para os dois cenários. Para mudar qualquer um deles, edite por lá.
        </p>
      </div>

      <div className="compare-summary" data-reveal>
        <div className="compare-cell">
          <span>Cenário A líquido</span>
          <strong>{money(resultA.net)}</strong>
        </div>
        <div className={`compare-cell compare-diff${up ? '' : ' down'}`}>
          <span><Icon name={up ? 'trending_up' : 'trending_down'} size={16} />Diferença mensal</span>
          <strong>{up ? '+' : '−'}{money(Math.abs(delta))}</strong>
          <small>{percent.toFixed(1)}% {up ? 'a mais' : 'a menos'} no líquido · {money(Math.abs(delta) * 12)} por ano</small>
        </div>
        <div className="compare-cell b">
          <span>Cenário B líquido</span>
          <strong>{money(resultB.net)}</strong>
        </div>
      </div>

      <div className="compare-switch">
        <Segmented
          label="Cenário exibido" value={shown} onChange={setShown}
          options={[{ value: 'a', label: 'Cenário A' }, { value: 'b', label: 'Cenário B' }]}
        />
      </div>

      <div className="compare-results" data-shown={shown}>
        <ResultCard data={resultA} badge="A" subtitle="Cenário A" className="compare-a" plain compact />
        <ResultCard data={resultB} badge="B" subtitle="Cenário B" className="compare-b b" compact />
      </div>
    </main>
  );
}
